import React, { useEffect, useMemo, useState } from 'react';
import {Button, Group, Modal, Radio, RadioGroup, Select, Textarea, TextInput, Tooltip} from '@mantine/core';
import {
  TransactionTeam, useCancelTransactionTeamMutation, useGetReasonCodesQuery,
} from '@repo/services';
import {useDisclosure} from "@mantine/hooks";
import {useForm, UseFormInput, UseFormReturnType} from "@mantine/form";
import {IconCircleX} from '@tabler/icons-react';
import {capitalizeFirstLetterOnly, createFormDataObject, useAgLazyQuery, useAgMutation, useNotes} from "@repo/utils";
import {notifications} from "@mantine/notifications";
import SubtleButton from '../../SubtleButton';

/**
 * A modal dialog for rejecting or failing a transaction team, plus a small trigger button that opens the modal.
 *
 * Derives reason codes from `useGetReasonCodesQuery` and a `useNotes` hook (tied to `id`). On submit the component writes a note, shows a notification, calls the cancel-team mutation, then closes the dialog.
 *
 * @param id - The transaction team ID to reject/fail. May be `undefined`.
 */
export default function TransactionRejectOrFailModal({ id }: { id: string | undefined }) {
    const [opened, { open, close }] = useDisclosure(false);
    const [cancelTeamCallbackObject, setCancelTeamCallbackObject] = useState<{
        selectedStatus: 'rejected' | 'failed';
        reasonString: string;
        notes: string | undefined;
    }>({
        selectedStatus: 'rejected',
        reasonString: '',
        notes: undefined
    });
    const { createNote } = useNotes({ id: id });
    const { data: reasonCodes = [] } = useGetReasonCodesQuery(null);
    const { trigger: triggerCancelTransactionTeam } = useAgMutation<TransactionTeam>(
        useCancelTransactionTeamMutation,
        () => {
            const noteTitle = `Transaction ${cancelTeamCallbackObject.selectedStatus}`;
            const noteDetails = `Reason: ${cancelTeamCallbackObject.reasonString}. ${
                cancelTeamCallbackObject.notes ? 'Additional Details: ' + cancelTeamCallbackObject.notes : ''
            }`;
            createNote({ title: noteTitle, message: noteDetails });
            notifications.show({
                title: `Transaction ${cancelTeamCallbackObject.selectedStatus}`,
                message: `Transaction ${id} ${cancelTeamCallbackObject.selectedStatus} due to: ${cancelTeamCallbackObject.reasonString}`
            });
        }
    );

    const formInputStyles = {
        marginTop: 20
    }

    const form = useForm({
        initialValues: {
            rejectOrFail: '',
            reason: '',
            otherDetails: '',
            notes: '',
        },

        validate: {
            rejectOrFail: (value) => (value ? null: 'You must select either "Reject" or "Fail"'),
            reason: (value) => (value ? null : 'Reason is required'),
            otherDetails: (value, values) =>
                values.reason === 'other' && !value ? 'Other details are required' : null,
        },
    });

    const handleSubmit = (values: any) => {
        form.validate();
        const selectedReasonCode = reasonCodes?.find((rc) => rc.aimReasonCode === values?.reason);
        if (selectedReasonCode) {
            const reasonString =
                selectedReasonCode?.aimReasonCode.toLowerCase() === 'other' ? `Other - ${values?.otherDetails}` : selectedReasonCode?.description;
            setCancelTeamCallbackObject({
                selectedStatus: values?.rejectOrFail,
                reasonString,
                notes: values?.notes
            });
            const data = createFormDataObject({
                rejectOrFailed: values?.rejectOrFail === 'rejected' ? 'R' : 'F',
                reasonCode: selectedReasonCode?.aimReasonCode,
                comment: values?.otherDetails
            });
            triggerCancelTransactionTeam({
                aimTransactionTeam: id,
                data: data
            });
        }
        handleClose();
    };

    const handleClose = () => {
        form.reset();
        close();
    }

    const cancelOptions = useMemo(
        () =>
            reasonCodes
                .filter((r) => (form.values.rejectOrFail === 'failed' ? r.validForFailedTeams : r.validForTeamRejections))
                .map((m) => {
                    return { key: m.aimReasonCode, value: m.aimReasonCode, label: capitalizeFirstLetterOnly(m.description) };
                }),
        [reasonCodes, form.values.rejectOrFail]
    );

    return (
        <>
            <Modal opened={opened} onClose={handleClose} title="Reject Or Fail Transaction">
                <form onSubmit={form.onSubmit((values) => handleSubmit(values))}>
                    <RadioGroup
                        name="rejectOrFail"
                        label="Reject or Fail"
                        defaultValue="rejected"
                        style={formInputStyles}
                        {...form.getInputProps('rejectOrFail')}
                    >
                        <Group>
                            <Radio value="rejected" label="Reject" />
                            <Radio value="failed" label="Fail" />
                        </Group>
                    </RadioGroup>

                    <Select
                        label="Reason"
                        name="reason"
                        data={cancelOptions}
                        required
                        style={formInputStyles}
                        {...form.getInputProps('reason')}
                    />

                    {form.values.reason?.toLowerCase() === 'other' && (
                        <TextInput
                            label="Other Details"
                            name="otherDetails"
                            required
                            style={formInputStyles}
                            {...form.getInputProps('otherDetails')}
                        />
                    )}

                    <Textarea
                        label="Notes"
                        name="notes"
                        required
                        style={formInputStyles}
                        {...form.getInputProps('notes')}
                    />

                    <Group style={formInputStyles}>
                        <Button
                            type="submit"
                        >
                            Submit
                        </Button>
                        <Button
                            variant='outline'
                            color='red'
                            onClick={handleClose}
                        >
                            Cancel
                        </Button>
                    </Group>

                </form>
            </Modal>

            <Tooltip label="Cancel">
                <SubtleButton buttonColor={'red'} text={'Cancel'} color='red' onClick={open} rightSection={<IconCircleX />} />
                {/*<Button variant={'subtle'} style={{ borderBottom: `2px solid red` }} onClick={open} color='red' rightSection={<IconCircleX />}>Cancel</Button>*/}
            </Tooltip>
        </>
    );
}
